// Peças do Editor do Mentor usadas por mais de uma aba (editor.tsx e
// editor-serie.tsx): os ícones e o recorte da foto.
import React, { useCallback, useEffect, useRef, useState } from 'react';

export const LADO_FOTO = 1024; // o servidor também guarda até 1024 px

const svg = (d: React.ReactNode) => <svg viewBox="0 0 24 24" aria-hidden="true">{d}</svg>;
const tr = { stroke: 'currentColor', strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
export const Ic = {
  left: svg(<path d="M15 5l-7 7 7 7" {...tr} strokeWidth={2.2} />),
  vg: svg(<><rect x="4" y="4" width="7" height="7" rx="1.5" {...tr} /><rect x="13" y="4" width="7" height="7" rx="1.5" {...tr} /><rect x="4" y="13" width="7" height="7" rx="1.5" {...tr} /><rect x="13" y="13" width="7" height="7" rx="1.5" {...tr} /></>),
  perfil: svg(<><circle cx="12" cy="8.5" r="3.6" {...tr} /><path d="M4.8 20c.9-3.6 3.7-5.5 7.2-5.5s6.3 1.9 7.2 5.5" {...tr} /></>),
  pagina: svg(<><rect x="5" y="3.5" width="14" height="17" rx="2" {...tr} /><path d="M8.5 8h7M8.5 12h7M8.5 16h4" {...tr} /></>),
  serie: svg(<><path d="M5 4.5h5.5a2 2 0 0 1 2 2V20a1.6 1.6 0 0 0-1.6-1.6H5z" {...tr} /><path d="M19 4.5h-5.5a2 2 0 0 0-2 2V20a1.6 1.6 0 0 1 1.6-1.6H19z" {...tr} /></>),
  clube: svg(<><circle cx="9" cy="9" r="3.2" {...tr} /><path d="M3.5 19c.7-3 2.8-4.6 5.5-4.6s4.8 1.6 5.5 4.6" {...tr} /><path d="M15.5 6.2a3 3 0 0 1 0 5.6M17.5 14.6c1.6.6 2.6 2 3 4.4" {...tr} /></>),
  olho: svg(<><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" {...tr} /><circle cx="12" cy="12" r="3" {...tr} /></>),
  camera: svg(<><path d="M4 8.5h3l1.6-2.5h6.8L17 8.5h3v10H4z" {...tr} /><circle cx="12" cy="13" r="3.3" {...tr} /></>),
  ok: svg(<path d="M5 12.5l4.2 4.2L19 7" {...tr} strokeWidth={2.2} />),
  ir: svg(<path d="M9 5l7 7-7 7" {...tr} strokeWidth={2} />),
  subir: svg(<path d="M6 15l6-6 6 6" {...tr} strokeWidth={2} />),
  descer: svg(<path d="M6 9l6 6 6-6" {...tr} strokeWidth={2} />),
  celular: svg(<><rect x="7" y="3" width="10" height="18" rx="2.2" {...tr} /><path d="M11 18h2" {...tr} /></>),
  computador: svg(<><rect x="3" y="4.5" width="18" height="12" rx="1.8" {...tr} /><path d="M8.5 20h7M12 16.5V20" {...tr} /></>),
  fechar: svg(<path d="M6 6l12 12M18 6L6 18" {...tr} strokeWidth={2} />),
  mais: svg(<path d="M12 5v14M5 12h14" {...tr} strokeWidth={2} />),
  lixo: svg(<><path d="M4.5 7h15M9.5 7V4.8h5V7M6.5 7l.9 12.5h9.2L17.5 7" {...tr} /><path d="M10.2 10.5v6M13.8 10.5v6" {...tr} /></>),
  lapis: svg(<path d="M4.5 19.5l1-4L15.8 5.2a1.9 1.9 0 0 1 2.7 0l.3.3a1.9 1.9 0 0 1 0 2.7L8.5 18.5z" {...tr} />),
  busca: svg(<><circle cx="10.5" cy="10.5" r="6" {...tr} /><path d="M15 15l5 5" {...tr} strokeWidth={2} /></>),
  varinha: svg(<><path d="M4.5 19.5L15 9" {...tr} strokeWidth={2} /><path d="M17 3.5v3M15.5 5h3M19.5 9.5v2M18.5 10.5h2M11 3.5v1.6M10.2 4.3h1.6" {...tr} /></>),
};

// O assistente "Montar meu Círculo" (editor.tsx) usa as abas Série e Clube como
// passos: no lugar da barra de salvar, elas mostram o rodapé do passo (Voltar e
// Avançar), e o Avançar grava pela mesma API antes de seguir.
export type RodapePasso = (o: { pronto: boolean; ocupado: boolean; avancar: () => void }) => React.ReactNode;

// ---- recorte: arrasta para posicionar, o controle aproxima ----
// `proporcao` = largura / altura da janela: 1 na foto do perfil (que a página
// mostra em círculo), 2/3 na capa do livro.
const JANELA = 260; // px na tela, no lado maior

export const Recorte: React.FC<{
  img: HTMLImageElement;
  onCancelar: () => void;
  onUsar: (b: Blob) => void;
  proporcao?: number;
  titulo?: string;
  nota?: string;
  circulo?: boolean;
}> = ({ img, onCancelar, onUsar, proporcao = 1, titulo = 'Recortar a foto', nota, circulo = true }) => {
  const w = img.naturalWidth, h = img.naturalHeight;
  const JW = proporcao >= 1 ? JANELA : Math.round(JANELA * proporcao);
  const JH = proporcao >= 1 ? Math.round(JANELA / proporcao) : JANELA;
  const escalaBase = Math.max(JW / w, JH / h);
  const [zoom, setZoom] = useState(1);
  const escala = escalaBase * zoom;
  const prender = useCallback((x: number, y: number, s: number) => ({
    x: Math.min(0, Math.max(JW - w * s, x)),
    y: Math.min(0, Math.max(JH - h * s, y)),
  }), [w, h, JW, JH]);
  const [pos, setPos] = useState(() => ({ x: (JW - w * escalaBase) / 2, y: (JH - h * escalaBase) / 2 }));
  const arrasto = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCancelar]);

  const mudarZoom = (z: number) => {
    // Aproxima mantendo o centro da janela no mesmo ponto da foto.
    const cx = (JW / 2 - pos.x) / escala, cy = (JH / 2 - pos.y) / escala;
    const s = escalaBase * z;
    setZoom(z);
    setPos(prender(JW / 2 - cx * s, JH / 2 - cy * s, s));
  };

  const usar = () => {
    setGerando(true);
    const lw = JW / escala, lh = JH / escala; // em pixels da foto
    const k = Math.min(1, LADO_FOTO / Math.max(lw, lh));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(lw * k)); c.height = Math.max(1, Math.round(lh * k));
    const ctx = c.getContext('2d');
    if (!ctx) { setGerando(false); return; }
    ctx.drawImage(img, -pos.x / escala, -pos.y / escala, lw, lh, 0, 0, c.width, c.height);
    c.toBlob((b) => { setGerando(false); if (b) onUsar(b); }, 'image/jpeg', 0.9);
  };

  return (
    <div className="ced-recorte" role="dialog" aria-modal="true" aria-label={titulo} data-recorte=""
      onClick={(e) => { if (e.target === e.currentTarget) onCancelar(); }}>
      <div className="ced-recorte-cartao">
        <h2>{titulo}</h2>
        <p className="ced-nota">{nota || 'Arraste para posicionar. O quadrado é o que vai para a página, em círculo.'}</p>
        <div className="ced-recorte-janela" style={{ width: JW, height: JH }}
          onPointerDown={(e) => { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); arrasto.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y }; }}
          onPointerMove={(e) => { const a = arrasto.current; if (a) setPos(prender(a.x + e.clientX - a.px, a.y + e.clientY - a.py, escala)); }}
          onPointerUp={() => { arrasto.current = null; }} onPointerCancel={() => { arrasto.current = null; }}>
          <img src={img.src} alt="" draggable={false}
            style={{ width: w * escala, height: h * escala, transform: `translate(${pos.x}px, ${pos.y}px)` }} />
          {circulo && <span className="ced-recorte-circulo" aria-hidden="true" />}
        </div>
        <label className="ced-recorte-zoom">
          <span>Aproximar</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => mudarZoom(Number(e.target.value))} data-zoom="" />
        </label>
        <div className="ced-linha ced-fim">
          <button className="ced-btn ced-btn-q" onClick={onCancelar}>Cancelar</button>
          <button className="ced-btn ced-btn-1" data-acao="usar-recorte" onClick={usar} disabled={gerando}>Usar esta foto</button>
        </div>
      </div>
    </div>
  );
};

// Abre o arquivo escolhido como imagem (para o recorte), ou diz o motivo.
export function abrirImagem(f: File, ok: (r: { url: string; img: HTMLImageElement }) => void, falha: (msg: string) => void) {
  if (!/^image\//.test(f.type)) { falha('Esse arquivo não é uma imagem. Use JPEG, PNG ou WebP.'); return; }
  const url = URL.createObjectURL(f);
  const img = new Image();
  img.onload = () => ok({ url, img });
  img.onerror = () => { URL.revokeObjectURL(url); falha('Não deu para abrir essa imagem. Use JPEG, PNG ou WebP.'); };
  img.src = url;
}
